import { SkillCategory } from '../../types/skillProgress';

export const golfSkillCategories: SkillCategory[] = [
  {
    id: 'full-swing',
    name: 'Full Swing',
    description: 'Master the fundamental golf swing',
    skills: [
      {
        id: 'grip-setup',
        name: 'Grip & Setup',
        description: 'Establish proper grip and address position',
        difficulty: 'beginner',
        tips: [
          'Use a neutral grip with both hands',
          'Stand with feet shoulder-width apart',
          'Keep your spine straight and tilted away from target',
          'Position ball in center of stance for irons'
        ]
      },
      {
        id: 'backswing',
        name: 'Backswing',
        description: 'Execute a proper backswing',
        difficulty: 'beginner',
        prerequisites: ['grip-setup'],
        tips: [
          'Take the club back low and slow',
          'Turn your shoulders, not just your arms',
          'Keep your left arm straight',
          'Complete your shoulder turn'
        ]
      },
      {
        id: 'downswing',
        name: 'Downswing',
        description: 'Execute a proper downswing',
        difficulty: 'intermediate',
        prerequisites: ['backswing'],
        tips: [
          'Start downswing with your lower body',
          'Keep your head behind the ball',
          'Maintain lag in your wrists',
          'Follow through completely'
        ]
      },
      {
        id: 'tempo-rhythm',
        name: 'Tempo & Rhythm',
        description: 'Develop consistent swing tempo',
        difficulty: 'intermediate',
        prerequisites: ['downswing'],
        tips: [
          'Maintain a smooth, even tempo',
          'Don\'t rush the downswing',
          'Practice with a metronome',
          'Focus on rhythm over power'
        ]
      }
    ]
  },
  {
    id: 'short-game',
    name: 'Short Game',
    description: 'Master shots around the green',
    skills: [
      {
        id: 'putting-basic',
        name: 'Basic Putting',
        description: 'Execute a proper putting stroke',
        difficulty: 'beginner',
        tips: [
          'Keep your head still',
          'Use a pendulum motion',
          'Keep your wrists firm',
          'Follow through straight toward target'
        ]
      },
      {
        id: 'chipping',
        name: 'Chipping',
        description: 'Execute controlled chip shots',
        difficulty: 'intermediate',
        prerequisites: ['putting-basic'],
        tips: [
          'Use a putting-like stroke',
          'Keep your weight on your front foot',
          'Use the bounce of the club',
          'Focus on landing spot, not the hole'
        ]
      },
      {
        id: 'pitching',
        name: 'Pitching',
        description: 'Execute high, soft pitch shots',
        difficulty: 'intermediate',
        prerequisites: ['chipping'],
        tips: [
          'Open your stance slightly',
          'Use a full swing with shorter follow-through',
          'Accelerate through impact',
          'Practice different distances'
        ]
      },
      {
        id: 'bunker-play',
        name: 'Bunker Play',
        description: 'Master sand shots',
        difficulty: 'advanced',
        prerequisites: ['pitching'],
        tips: [
          'Open your stance and clubface',
          'Aim to hit sand, not the ball',
          'Follow through completely',
          'Practice different lies and distances'
        ]
      }
    ]
  },
  {
    id: 'course-management',
    name: 'Course Management',
    description: 'Develop strategic thinking on the course',
    skills: [
      {
        id: 'club-selection',
        name: 'Club Selection',
        description: 'Choose the right club for each shot',
        difficulty: 'beginner',
        tips: [
          'Know your average distances',
          'Consider wind and elevation',
          'Choose the safest option',
          'Practice with all your clubs'
        ]
      },
      {
        id: 'target-selection',
        name: 'Target Selection',
        description: 'Choose the right target for each shot',
        difficulty: 'intermediate',
        prerequisites: ['club-selection'],
        tips: [
          'Aim for the center of the green',
          'Avoid trouble areas',
          'Consider pin placement',
          'Play to your strengths'
        ]
      },
      {
        id: 'risk-assessment',
        name: 'Risk Assessment',
        description: 'Evaluate risk vs. reward on each shot',
        difficulty: 'intermediate',
        prerequisites: ['target-selection'],
        tips: [
          'Consider your current score',
          'Evaluate the situation',
          'Choose conservative options when appropriate',
          'Take calculated risks when needed'
        ]
      },
      {
        id: 'advanced-strategy',
        name: 'Advanced Strategy',
        description: 'Develop sophisticated course strategy',
        difficulty: 'advanced',
        prerequisites: ['risk-assessment'],
        tips: [
          'Study the course layout',
          'Plan your round in advance',
          'Adapt to conditions',
          'Manage your emotions'
        ]
      }
    ]
  },
  {
    id: 'mental-game',
    name: 'Mental Game',
    description: 'Develop mental toughness and focus',
    skills: [
      {
        id: 'pre-shot-routine',
        name: 'Pre-Shot Routine',
        description: 'Develop a consistent pre-shot routine',
        difficulty: 'beginner',
        tips: [
          'Visualize your shot',
          'Take practice swings',
          'Align yourself properly',
          'Stay relaxed and confident'
        ]
      },
      {
        id: 'focus-concentration',
        name: 'Focus & Concentration',
        description: 'Maintain focus throughout the round',
        difficulty: 'intermediate',
        prerequisites: ['pre-shot-routine'],
        tips: [
          'Stay in the present moment',
          'Don\'t dwell on bad shots',
          'Focus on the next shot',
          'Use breathing techniques'
        ]
      },
      {
        id: 'pressure-handling',
        name: 'Pressure Handling',
        description: 'Perform well under pressure',
        difficulty: 'advanced',
        prerequisites: ['focus-concentration'],
        tips: [
          'Practice pressure situations',
          'Stay positive and confident',
          'Use visualization techniques',
          'Develop mental toughness'
        ]
      }
    ]
  },
  {
    id: 'physical-fitness',
    name: 'Physical Fitness',
    description: 'Develop golf-specific fitness',
    skills: [
      {
        id: 'flexibility',
        name: 'Flexibility',
        description: 'Improve flexibility for better swing',
        difficulty: 'beginner',
        tips: [
          'Stretch before and after play',
          'Focus on shoulder and hip mobility',
          'Practice yoga or pilates',
          'Maintain good posture'
        ]
      },
      {
        id: 'core-strength',
        name: 'Core Strength',
        description: 'Develop core strength for power',
        difficulty: 'intermediate',
        prerequisites: ['flexibility'],
        tips: [
          'Practice planks and crunches',
          'Use resistance bands',
          'Focus on rotational exercises',
          'Build endurance'
        ]
      },
      {
        id: 'balance-coordination',
        name: 'Balance & Coordination',
        description: 'Improve balance and coordination',
        difficulty: 'intermediate',
        prerequisites: ['core-strength'],
        tips: [
          'Practice on unstable surfaces',
          'Use balance boards',
          'Work on single-leg exercises',
          'Improve proprioception'
        ]
      }
    ]
  }
];
